import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useRoutePath } from "../../../hooks/useRoutePath";
import getAccessHierachyView from "../../../utils/getAccessHierachyView";
import { AssetStatusArchiving, AssetStatusArchived, AssetStatusRestoring } from "../AssetStatus";
import { MediaImage } from "../components";
import { Box, Checkbox, Grid, Stack, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import AssetTypeFlag from "@components_v2/icon-type/asset/asset-type-flag";
import { IconAi, IconDrm, IconLink, IconLock, IconNew, IconStar, IconVoice } from "@mzc-cloudplex/icons";
import { alpha } from "@mui/material";
import menuService from "@services/menuService";
import { useSelector } from "react-redux";
import MediaImageWrapper from "@routes/asset/components/media-image-wrapper";
import { useLocation } from "react-router";
import { ASSET_PREVIEW_STATUS, ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE } from "@constants";
import { CircledIcon } from "@components_v2/icon";
import countDaysBetweentDates from "@cores/countDaysBetweentDates";

const AssetTableCard = ({
    asset,
    stageVersion,
    onChangeSelectItem,
    selectedItems,
    checkable,
    thumbnailCrop,
    onClickName,
}) => {
    const v = asset;
    const routePath = useRoutePath();
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);

    const project = useSelector(({ project }) => project);
    const stage = useSelector(({ stage }) => stage);
    const [isShowImagePreviewModal, setIsShowImagePreviewModal] = useState(false);

    const handleShowImagePreviewModal = useCallback(() => {
        setIsShowImagePreviewModal(true);
    }, [isShowImagePreviewModal]);

    const handleCloseImagePreviewModal = useCallback(() => {
        setIsShowImagePreviewModal(false);
    }, [isShowImagePreviewModal]);

    const assetDetailPath = useMemo(() => {
        if (onClickName) return onClickName(asset);
        else
            return `${menuService.paths.assetsAssetDetail(spaceId, projectId, asset.id)}?projectId=${
                asset.project?.id
            }`;
    }, [spaceId, projectId, asset]);

    return (
        <div className="list-content">
            <Grid container alignItems={"center"} flexWrap={"nowrap"}>
                {getAccessHierachyView(stageVersion) && checkable && (
                    // TODO: assets.isActiveView 에 따라서 안보이게 되므로 해당 라인 주석
                    <Checkbox
                        color={"greySecondary"}
                        checked={!!selectedItems.find((item) => item.id === v.id)}
                        onChange={() => onChangeSelectItem(v)}
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    />
                )}
                <MediaImageWrapper
                    status={v.previews?.filter((p) => p.status !== ASSET_PREVIEW_STATUS.ERROR)?.[0]?.status}
                    sx={{
                        position: `relative`,
                        minWidth: ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE,
                        width: ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE,
                        height: ASSET_THUMBNAIL_TABLE_CONTAINER_SIZE,
                        borderRadius: `4px`,
                        backgroundColor: alpha(theme.palette.greySecondary[50], 0.5),
                        ml: 1,
                        mr: 1.5,
                    }}
                >
                    <MediaImage
                        variant={"table"}
                        errorIconProps={{ position: "left", size: 16 }}
                        asset={v}
                        thumbnailCrop={thumbnailCrop}
                        isShowImagePreviewModal={isShowImagePreviewModal}
                        handleShowImagePreviewModal={handleShowImagePreviewModal}
                        handleCloseImagePreviewModal={handleCloseImagePreviewModal}
                    />
                    {v.status === "Archived" && (
                        <AssetStatusArchived containerType={"archive"} status={v.archiveStatus} variant={"table"} />
                    )}
                    {v.status === "Archiving" && <AssetStatusArchiving containerType={"archive"} />}
                    {v.status === "Restoring" && <AssetStatusRestoring containerType={"archive"} />}
                    {countDaysBetweentDates({ fromDate: asset.createdAt }) < 7 && (
                        <Box
                            sx={{
                                position: "absolute",
                                right: theme.spacing(-0.25),
                                bottom: theme.spacing(-0.25),
                                color: theme.palette.error.main,
                            }}
                        >
                            <IconNew size={34} viewBox={"0 0 34 16"} style={{ height: "16px" }} />
                        </Box>
                    )}
                </MediaImageWrapper>
                <Stack
                    sx={{
                        flex: 1,
                        overflow: "hidden",
                    }}
                >
                    <Grid container alignItems={"center"} flexWrap={"nowrap"} sx={{ overflow: `hidden` }} width={1}>
                        <AssetTypeFlag minWidth={22} width={22} height={16} type={asset.mediaType} />
                        <Grid item xs sx={{ textOverflow: `ellipsis`, overflow: `hidden` }}>
                            <Typography variant={"body1_500"} title={asset.name} noWrap>
                                <Link
                                    to={assetDetailPath}
                                    style={{ color: `inherit`, ":hover": { textDecoration: `none` } }}
                                >
                                    {asset.name}
                                </Link>
                            </Typography>
                        </Grid>
                    </Grid>
                    <Grid container alignItems={"center"} gap={0.75} mt={1} flexWrap={"nowrap"}>
                        <Grid item xs>
                            <Grid container>
                                <Typography variant={"body3_400"} color={theme.palette.text.secondary}>
                                    {asset.extensions ? asset.extensions : "-"}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Stack direction={"row"} gap={0.5} sx={{ alignItems: "center" }}>
                                {asset.isConnected ? <IconLink size={16} /> : ""}
                                {asset.myFavorite ? <IconStar size={16} htmlColor={theme.palette.etc.orange500} /> : ""}
                                {asset.hasDubbing && (
                                    <CircledIcon size={20} color={theme.palette.etc.purple400}>
                                        <IconVoice size={12} htmlColor={theme.palette.constant.white} />
                                    </CircledIcon>
                                )}
                                {asset.encryptions?.indexOf("DRM") > -1 && (
                                    <CircledIcon size={20} color={theme.palette.warning.main}>
                                        <IconDrm size={12} htmlColor={theme.palette.constant.white} />
                                    </CircledIcon>
                                )}
                                {asset.isAnalyzed && (
                                    <CircledIcon size={20} color={theme.palette.success.light}>
                                        <IconAi size={12} htmlColor={theme.palette.constant.white} />
                                    </CircledIcon>
                                )}
                                {asset.isOwnerLocked && (
                                    <CircledIcon size={20} color={theme.palette.etc.gray300}>
                                        <IconLock size={12} htmlColor={theme.palette.constant.white} />
                                    </CircledIcon>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Stack>
            </Grid>
        </div>
    );
};

export default AssetTableCard;
